// Global Command Palette (Cmd/Ctrl+K).
// Quick search + jump across all entities: tx / categories / budgets /
// recurring / credit cards. Designed for keyboard power use on desktop
// but works on mobile too (tap search icon in TopBar).
//
// Search is client-side over already-fetched data (cached briefly). For
// large datasets this should be server-driven, but our app only has a few
// hundred rows tops, so in-memory fuzzy search is fine.
//
// Phase 117: the date-token parser + date-matcher helpers were extracted
// to lib/commandPalette.js so they can be unit-tested without a JSX
// runtime. Component imports them below.
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { data as dataService } from '../lib/dataService.js';
import { formatMoney } from '../lib/format.js';
import { useI18n } from '../i18n/index.jsx';
import { categoryLabel } from '../lib/categories.js';
import { parseQueryTokens, dateMatches } from '../lib/commandPalette.js';

const SHORTCUT_LABEL = navigator?.platform?.toLowerCase().includes('mac')
  ? '⌘K' : 'Ctrl+K';

export default function CommandPalette({ open, onClose }) {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const [searchData, setSearchData] = useState({ txs: [], cats: [], budgets: [], recurring: [], cards: [] });
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  // Fetch all data on open (cheap, < 1KB JSON).
  // Phase 35b: reads come from IndexedDB (instant, offline-capable).
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      dataService.getTransactions({ limit: 50 }).catch(() => []),
      dataService.getCategories().catch(() => []),
      dataService.getBudgets().catch(() => []),
      dataService.getRecurring().catch(() => []),
      dataService.getCreditCards().catch(() => []),
    ]).then(([txs, cats, budgets, recurring, cards]) => {
      setSearchData({ txs, cats, budgets, recurring, cards });
      setLoading(false);
    });
  }, [open]);

  // Autofocus input when opened.
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Build results based on current query. Query may contain date tokens
  // (YYYY-MM-DD, YYYY-MM, YYYY, YYYYMMDD, YYYYMM, MM-DD, / . separators)
  // and free-text tokens; both kinds are AND'd.
  const results = useMemo(() => {
    const { dates, texts } = parseQueryTokens(q);
    const textQuery = texts.join(' ').trim();
    if (!dates.length && !textQuery) {
      // Empty query → show navigation shortcuts.
      return [
        { type: 'nav', label: `${t('cmd.goTo')} ${t('nav.dashboard')}`, icon: '🏠', route: '/' },
        { type: 'nav', label: `${t('cmd.goTo')} ${t('nav.transactions')}`, icon: '📋', route: '/transactions' },
        { type: 'nav', label: `${t('cmd.goTo')} ${t('nav.budgets')}`, icon: '💳', route: '/budgets' },
        { type: 'nav', label: `${t('cmd.goTo')} ${t('nav.settings')}`, icon: '⚙️', route: '/settings' },
        { type: 'action', label: t('cmd.newTx'),     icon: '➕', route: '/add' },
      ];
    }
    const out = [];
    // Transactions: match on category name or note (search both raw + localized).
    for (const tx of searchData.txs) {
      const localizedName = categoryLabel({ name: tx.category_name }, t);
      const haystack = `${tx.category_name} ${localizedName} ${tx.note || ''}`.toLowerCase();
      if (dateMatches(tx.occurred_on, dates) && (textQuery === '' || haystack.includes(textQuery))) {
        // Use react-router `location.state` to carry the full tx object
        // across the route change. More reliable than ?focus=<id>: the page
        // doesn't need a second IndexedDB lookup to find the row, and the
        // tx's `occurred_on` is already in hand to set the month filter.
        out.push({
          type: 'tx',
          label: `${tx.category_icon} ${localizedName}${tx.note ? ' · ' + tx.note : ''}`,
          sub: tx.occurred_on + ' · ' + formatMoney(tx.amount, tx.currency || '$'),
          action: () => nav('/transactions', { state: { focusTx: tx } }),
        });
      }
    }
    // Categories: match on raw + localized name.
    for (const c of searchData.cats) {
      const localizedName = categoryLabel(c, t);
      const hay = `${c.name} ${localizedName}`.toLowerCase();
      if (textQuery === '' || hay.includes(textQuery)) {
        out.push({
          type: 'category',
          label: `${c.icon} ${localizedName}`,
          sub: c.type === 'expense' ? t('cmd.expenseCat') : t('cmd.incomeCat'),
          action: () => nav('/settings'),
        });
      }
    }
    // Budgets: match via category name (raw + localized).
    const catById = Object.fromEntries(searchData.cats.map(c => [c.id, c]));
    for (const b of searchData.budgets) {
      const c = catById[b.category_id];
      if (!c) continue;
      const localizedName = categoryLabel(c, t);
      const hay = `${c.name} ${localizedName}`.toLowerCase();
      if (textQuery === '' || hay.includes(textQuery)) {
        out.push({
          type: 'budget',
          label: `${c.icon} ${localizedName} ${t('cmd.budget')}`,
          sub: formatMoney(b.monthly_limit, '$') + ' ' + t('cmd.perMonth'),
          action: () => nav('/budgets'),
        });
      }
    }
    // Recurring rules: match on note or category name (raw + localized).
    for (const r of searchData.recurring) {
      const cat = catById[r.category_id];
      const localizedName = cat ? categoryLabel(cat, t) : '';
      const hay = `${r.note || ''} ${cat?.name || ''} ${localizedName}`.toLowerCase();
      if (textQuery === '' || hay.includes(textQuery)) {
        out.push({
          type: 'recurring',
          label: `🔁 ${cat?.icon || ''} ${localizedName || t('common.unknown')}`,
          sub: `${t('cmd.perMonth')} ${r.day_of_month} ${t('cmd.dayOfMonth')} · ${formatMoney(r.amount, '$')}`,
          action: () => nav('/settings'),
        });
      }
    }
    // Credit cards.
    for (const card of searchData.cards) {
      if (textQuery === '' || card.name.toLowerCase().includes(textQuery)) {
        out.push({
          type: 'card',
          label: `💳 ${card.name}`,
          sub: `${formatMoney(card.outstanding_amount || 0, '$')} ${t('cmd.outstanding')}`,
          action: () => nav('/settings'),
        });
      }
    }
    return out.slice(0, 50);
  }, [q, searchData, nav]);

  // Reset highlight when results change.
  useEffect(() => { setActiveIdx(0); }, [q]);

  const run = useCallback((item) => {
    if (!item) return;
    if (item.action) item.action();
    else if (item.route) nav(item.route);
    onClose();
  }, [nav, onClose]);

  // Keyboard nav.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter')     { e.preventDefault(); run(results[activeIdx]); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, activeIdx, run, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-[10vh] sm:pt-[20vh]"
      onClick={onClose}
      role="dialog"
      aria-label={t('cmd.ariaLabel')}
    >
      <div
        className="bg-white dark:bg-slate-800 w-full sm:max-w-xl sm:rounded-xl rounded-t-2xl shadow-2xl flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <span className="text-slate-400 dark:text-slate-500" aria-hidden="true">🔍</span>
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('cmd.searchPlaceholder')}
            className="flex-1 bg-transparent text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none text-sm"
            aria-label={t('common.search')}
          />
          <kbd className="hidden sm:inline-block text-[10px] text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-6 text-center text-sm text-slate-400">{t('cmd.loading')}</div>}
          {!loading && results.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">{t('cmd.noResults', { q })}</div>
          )}
          {!loading && results.map((item, i) => (
            <button
              key={i}
              onClick={() => run(item)}
              onMouseEnter={() => setActiveIdx(i)}
              className={[
                'w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm',
                activeIdx === i ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
              ].join(' ')}
            >
              {item.icon && <span aria-hidden="true">{item.icon}</span>}
              {!item.icon && <span aria-hidden="true" className="text-slate-400">•</span>}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{item.label}</div>
                {item.sub && <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{item.sub}</div>}
              </div>
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-[10px] text-slate-400 dark:text-slate-500 flex items-center justify-between">
          <span>{t('cmd.footerHint')}</span>
          <span className="hidden sm:inline">{SHORTCUT_LABEL} {t('cmd.toOpen')}</span>
        </div>
      </div>
    </div>
  );
}